import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TextField, Button, Checkbox, FormControlLabel } from '@mui/material';
import { ConfigContext, http } from '../../base-ui/lib';
import { toastError, toastSuccess } from '../../Toaster/Toast';

function MaterialDetailPage() {
	const { config } = useContext(ConfigContext);
	const { materialId } = useParams();
	const navigate = useNavigate();
	const isAddMode = materialId === 'add';

	// Initialize state with correct types
	const [material, setMaterial] = useState({
		obid: '',
		name: '',
		price: 0, // Initialize as number
		archive: false,
		reface: false,
		category: '',
	});

	useEffect(() => {
		const fetchMaterial = async () => {
			if (!isAddMode) {
				try {
					const response = await http.get(
						{},
						`${config.uri.base}/material/${materialId}`
					);
					setMaterial((prevMaterial) => ({
						...prevMaterial,
						...response,
						price: parseFloat(response.price) || 0, // Ensure price is a float
						archive: Boolean(response.archive), // Ensure booleans
						reface: Boolean(response.reface), // Ensure booleans
					}));
				} catch (error) {
					console.error('Error fetching material:', error);
				}
			}
		};

		fetchMaterial();
	}, [isAddMode, config.uri.base, materialId]);

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		let newValue;

		if (type === 'checkbox') {
			newValue = checked;
		} else if (name === 'price') {
			newValue = value === '' ? '' : parseFloat(value);
		} else {
			newValue = value;
		}

		setMaterial((prev) => ({
			...prev,
			[name]: newValue,
		}));
	};

	const handleSave = async () => {
		try {
			// Prepare the data to ensure correct types
			const dataToSend = {
				...material,
				price:
					typeof material.price === 'string'
						? parseFloat(material.price)
						: material.price,
				archive: Boolean(material.archive),
				reface: Boolean(material.reface),
			};

			if (isAddMode) {
				// Determine if adding a new material
				const response = await http.post(
					dataToSend,
					`${config.uri.base}/material`
				);
				if (response.errored)
					return toastError({
						title: 'Error',
						description: `${response.error.message}`,
					});

				toastSuccess({
					title: 'Material added successfully!',
					description: `${material.name} has been created.`,
				});
				navigate('/materials'); // Redirect to /materials after a short delay
			} else {
				// Updating an existing material
				const response = await http.put(
					dataToSend,
					`${config.uri.base}/material/${materialId}`
				);
				if (response.errored)
					return toastError({
						title: 'Error',
						description: `${response.error.message}`,
					});

				toastSuccess({
					title: 'Material updated successfully!',
					description: `${material.name} has been updated.`,
				});
			}
		} catch (error) {
			console.error(
				`Error ${isAddMode ? 'adding' : 'updating'} material:`,
				error
			);
			toastError({
				title: 'Error',
				description: `An error occurred while ${isAddMode ? 'adding' : 'updating'} the material.`,
			});
		}
	};

	return (
		<div>
			<h2>{isAddMode ? 'Add New Material' : 'Edit Material'}</h2>
			<form noValidate autoComplete="off">
				<TextField
					label="OBID"
					name="obid"
					value={material.obid}
					onChange={handleChange}
					fullWidth
					margin="normal"
					variant="outlined"
					disabled={!isAddMode}
				/>
				<TextField
					label="Name"
					name="name"
					value={material.name}
					onChange={handleChange}
					fullWidth
					margin="normal"
					variant="outlined"
					disabled={!isAddMode}
				/>

				<FormControlLabel
					control={
						<Checkbox
							name="archive"
							checked={material.archive}
							onChange={handleChange}
						/>
					}
					label="Archive"
					disabled={!isAddMode}
				/>
				<FormControlLabel
					control={
						<Checkbox
							name="reface"
							checked={material.reface}
							onChange={handleChange}
						/>
					}
					label="Reface"
					disabled={!isAddMode}
				/>
				<TextField
					label="Category"
					name="category"
					value={material.category}
					onChange={handleChange}
					fullWidth
					margin="normal"
					variant="outlined"
					disabled={!isAddMode}
				/>

				<TextField
					label="Price"
					name="price"
					type="number"
					value={material.price}
					onChange={handleChange}
					fullWidth
					margin="normal"
					variant="outlined"
				/>

				<Button onClick={handleSave} variant="contained" color="primary">
					{isAddMode ? 'Create Material' : 'Save Changes'}
				</Button>
			</form>
		</div>
	);
}

export default MaterialDetailPage;
